import Directions from "./directions";

/**
 * Dispatch a game event with a custom log message.
 * 
 * @param {string} actor
 * @param {string} message
 */
function logEvent(actor, message) {
  document.dispatchEvent(new CustomEvent("game.event", {
    detail: {
      actor,
      message
    }
  }));
}

/**
 * Chill for a turn and rest. Restores HP.
 * 
 * @param {Entity} entity 
 * @param {null} detail 
 * @param {Stage} stage 
 * @returns 
 */
function restAction(entity, detail, stage) {
  const enemies = stage.visibleEntities().filter(en => en.isEnemy());

  if (enemies.length > 0) {
    logEvent(entity.name, "Rested for a turn but can’t heal when monsters are nearby");
    return;
  }

  if (entity.health.hp < entity.health.max) {
    const restedHp = 1 + entity.stats.toughnessModifier;
    const restoreHealth = Math.min(entity.health.hp + restedHp, entity.health.max);
    entity.health.hp = restoreHealth;
    logEvent(entity.name, `Rested for a turn and restored ${restedHp}`);
  }

  logEvent(entity.name, "Rested for a turn");
}

/**
 * Pick up the top-most item from the stack at the current tile.
 * 
 * @param {Entity} entity 
 * @param {array<Item>} items 
 * @param {Stage} stage 
 */
function getAction(entity, items, stage) {
  // TODO: launch dialog if multiple entities or smarter logic for pickups
  const item = items[items.length - 1];
  if (stage.player.equipment.canAddItem(item)) {
    stage.removeEntity(item);
    item.x = null;
    item.y = null;
    stage.player.equipment.addItem(item);
    logEvent(entity.name, `Picked up ${item.name}`);
  } else {
    console.log("Cant add item")
  }
}

/**
 * Drop the currently equipped item onto the ground at the current tile.
 * 
 * @param {Entity} entity 
 * @param {Object} detail 
 * @param {Stage} stage 
 */
function dropAction(entity, detail, stage) {
  const { slot, index } = detail;
  const item = entity.equipment.dropItem(slot, index);

  if (item) {
    stage.spawnEntity(item, entity.x, entity.y);
    logEvent(entity.name, `Dropped ${item.name}`);
  }
}

/**
 * Fire a ranged weapon at a target.
 * 
 * @param {Entity} entity 
 * @param {Point} target 
 * @param {Stage} stage 
 * @returns 
 */
function fireAction(entity, target, stage) {
  const targetEntities = stage.entitiesAt(target.x, target.y);

  if (targetEntities.length == 0) {
    logEvent(entity.name, `${entity.name} fired a shot`);
  }

  for (let occupant of targetEntities) {
    if (occupant.isBlocking()) {
      occupant.shotFrom(entity);
      return;
    }
  }
  entity.consumeShot();
}

/**
 * Main action to handle directional movement input. If the entity has a transport
 * component then use it to sail. Otherwise assume it is walking and move that way.
 * 
 * @param {Entity} entity 
 * @param {Point} direction 
 * @param {Stage} stage 
 * @returns 
 */
function moveAction(entity, direction, stage) {
  if (entity.hasOwnProperty("transport")) {
    if (entity.transport.isSailing()) {
      sailAction(entity, direction, stage);
      return;
    }
  }

  walkAction(entity, direction, stage);
}

/**
 * Movement action for sailing on the water using transport via the boat entity. This
 * could be adapted to use the directional heading mechanics and wind direction/tacking
 * from the Vaka prototype. Current implementation is more suited to rowboats or steamships
 * than sailboats.
 * 
 * @param {Entity} entity 
 * @param {Point} direction 
 * @param {Stage} stage 
 * @returns 
 */
function sailAction(entity, direction, stage) {
  const mx = entity.x + direction.x;
  const my = entity.y + direction.y;

  if (stage.canSailTo(mx, my)) {
    for (let occupant of stage.entitiesAt(mx, my)) {
      if (occupant.isBlocking()) {
        occupant.bumpFrom(entity, stage);
        return;
      }
    }

    stage.moveEntityTo(entity, mx, my);
    stage.refreshVisibility();
  } else if (stage.canDockAt(mx, my)) {
    entity.transport.boat.dockBoat(entity, stage);
    stage.moveEntityTo(entity, mx, my);
    stage.refreshVisibility();
  }
}

/**
 * Handles walking over ground, checking for blocking entities and bump interactions
 * when needed. 
 * 
 * @param {Entity} entity 
 * @param {Point} direction 
 * @param {Stage} stage 
 * @returns 
 */
function walkAction(entity, direction, stage) {
  const mx = entity.x + direction.x;
  const my = entity.y + direction.y;

  // Always bump an entity if it is on the directed tile, regardless of transport mode
  // or whether the tile is traversable.
  for (let occupant of stage.entitiesAt(mx, my)) {
    if (occupant.isBlocking()) {
      occupant.bumpFrom(entity, stage);
      return;
    }
  }

  if (stage.canWalkTo(mx, my)) {
    stage.moveEntityTo(entity, mx, my);
    stage.refreshVisibility();
  } else if (stage.canInteractWith(mx, my)) {
    // TODO: Can only open door tiles for now
    stage.openDoor(mx, my);

    const label = stage.map[my][mx].name
      .replace(/[0-9]/g, "")
      .replace(/([a-z])([A-Z])/g, "$1 $2");

    logEvent(entity.name, `Opened ${label}`);
  }
}

function openAction(entity, detail, stage) {
  const { target } = detail;
  stage.openDoor(target.x, target.y);

  const label = stage.map[target.y][target.x].name
    .replace(/[0-9]/g, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  logEvent(entity.name, `Closed ${label}`);
}

function closeAction(entity, detail, stage) {
  const { target } = detail;
  stage.closeDoor(target.x, target.y);

  const label = stage.map[target.y][target.x].name
    .replace(/[0-9]/g, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2");

  logEvent(entity.name, `Closed ${label}`);
}

export {
  getAction,
  dropAction,
  moveAction,
  walkAction,
  fireAction,
  restAction,
  openAction,
  closeAction
};
